import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Zap,
  Upload,
  Sparkles,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Wand2,
  Calendar,
  Target,
  Palette,
  MessageSquare,
  Eye,
  Download,
  Share2,
  Heart,
  HelpCircle,
  Facebook,
  Instagram,
  Linkedin,
  Twitter
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useClient } from '../contexts/ClientContext';
import { useMobile } from '../hooks/useMobile';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useAITask } from '../contexts/AITaskContext';
import { getApiUrl } from '../utils/config';
import mediaService from '../services/mediaService';
import AuthenticatedImage from '../components/media/AuthenticatedImage';
import { MultiMediaFileSelector } from '../components/media/MultiMediaFileSelector';
import { MediaFile } from '../components/media/types';
import { ModelSelector } from '../components/ai/ModelSelector';
import ComingSoonWrapper from '../components/launch/ComingSoonWrapper';
import { useUserTier } from '../hooks/useUserTier';
import { useModels } from '../hooks/useModels';
import { useAITaskCosts } from '../hooks/useAITaskCosts';
import { UpgradeModal } from '../components/modals/UpgradeModal';
import { Crown } from 'lucide-react';
import { useTour } from '../contexts/TourContext';
import { oneClickCreatorSteps } from '../components/onboarding/tour-steps';

// Type definitions
interface BrandAnalysis {
  brand_voice: string;
  primary_colors: string[];
  visual_style: string;
  ad_intent: string;
  optimal_platforms: string[];
  posting_times: { [key: string]: string };
  target_audience?: string;
  industry?: string;
  brand_personality?: string[];
  primary_goal?: string;
  brand_name?: string;
  logo_url?: string;
  logo_media_id?: string;
  has_connected_accounts: boolean;
  all_platforms_connected: boolean;
}

interface PostsResult {
  success: boolean;
  message: string;
  campaign_id: string;
  enhanced_image_url?: string;
  generated_posts: Array<{
    id: string;
    platform: string;
    content: string;
    content_type: string;
    media_file_id: string;
    scheduled_at?: string;
    status: string;
  }>;
  scheduled_times: { [key: string]: string | null };
  processing_time_seconds?: number;
  was_scheduled: boolean;
  post_group_id?: string;
  brand_analysis?: BrandAnalysis;
}

const OneClickCreatorPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentClient } = useClient();
  const isMobile = useMobile();
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const { addTask, getTasksByPage, isTaskRunning, getRecentCompletedTask, updateTask, removeTask } = useAITask();
  const { restartTour } = useTour();

  // State management
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedMediaId, setUploadedMediaId] = useState<string | null>(null);
  const [selectedMediaFromLibrary, setSelectedMediaFromLibrary] = useState<MediaFile[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [scheduleImmediately, setScheduleImmediately] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creationProgress, setCreationProgress] = useState(0);
  const [brandAnalysis, setBrandAnalysis] = useState<BrandAnalysis | null>(null);
  const [postsResult, setPostsResult] = useState<PostsResult | null>(null);
  const [currentStep, setCurrentStep] = useState<'upload' | 'analyze' | 'create' | 'complete'>('upload');
  const [creationMode, setCreationMode] = useState<'image' | 'brand'>('image');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Social media connection state
  const [hasConnectedAccounts, setHasConnectedAccounts] = useState<boolean | null>(null);
  const [isCheckingConnections, setIsCheckingConnections] = useState(true);
  const [connectedPlatforms, setConnectedPlatforms] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  
  // Check if user is on free tier
  const { isFreeTier } = useUserTier();
  const isBetaTester = user?.is_beta_tester || false;
  const isOneClickLocked = isFreeTier && !isBetaTester;

  // Track notification IDs to prevent duplicate toasts
  const notifiedTaskIds = useRef(new Set<string>());

  // Track if user explicitly wants to start fresh
  const [startFresh, setStartFresh] = useState(false);

  // Track if this is a fresh page load (refresh) vs navigation
  const [isPageRefresh, setIsPageRefresh] = useState(() => {
    // Check if this is a page refresh by looking at navigation type
    return performance.navigation?.type === 1 ||
      performance.getEntriesByType('navigation')[0]?.type === 'reload';
  });

  // Check for connected social media accounts
  const checkConnectedAccounts = async () => {
    if (!currentClient) return;
    
    setIsCheckingConnections(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/social/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
      });
      
      if (response.ok) {
        const connections = await response.json();
        const hasConnections = connections && connections.length > 0;
        const platformNames = connections.map((conn: any) => conn.provider.toLowerCase());
        
        setHasConnectedAccounts(hasConnections);
        setConnectedPlatforms(platformNames);
        
        // Auto-select all connected platforms by default if none are selected
        if (hasConnections && selectedPlatforms.length === 0) {
          setSelectedPlatforms(platformNames);
        }
        
        console.log('🔗 Social media connections check:', hasConnections ? `Connected (${connections.length} accounts)` : 'No connections');
        console.log('🔗 Connected platforms:', platformNames);
      } else {
        console.error('Failed to check social media connections:', response.status, response.statusText);
        setHasConnectedAccounts(false);
        setConnectedPlatforms([]);
        setSelectedPlatforms([]);
      }
    } catch (error) {
      console.error('Error checking social media connections:', error);
      setHasConnectedAccounts(false);
      setConnectedPlatforms([]);
      setSelectedPlatforms([]);
    } finally {
      setIsCheckingConnections(false);
    }
  };

  // Check for connected accounts on page load and when client changes
  React.useEffect(() => {
    if (currentClient) {
      checkConnectedAccounts();
    }
  }, [currentClient]);

  // Get available models
  const { availableModels } = useModels();

  // Get AI task costs
  const { getTaskCost } = useAITaskCosts();

  // Auto-select Gemini 3 Pro model when models load
  React.useEffect(() => {
    if (availableModels.length > 0 && !selectedModelId) {
      // Find Gemini 3 Pro model (name: 'gemini-pro')
      const geminiProModel = availableModels.find(m => m.name === 'gemini-pro');
      
      if (geminiProModel) {
        console.log('🎯 OneClickCreator: Auto-selecting Gemini 3 Pro model:', geminiProModel.display_name, geminiProModel.model_id);
        setSelectedModelId(geminiProModel.model_id);
      } else {
        console.log('⚠️ OneClickCreator: Gemini 3 Pro model not found, using default model');
        // Fallback to default model if Gemini 3 Pro is not available
        const defaultModel = availableModels.find(m => m.is_default);
        if (defaultModel) {
          console.log('🎯 OneClickCreator: Using default model:', defaultModel.display_name, defaultModel.model_id);
          setSelectedModelId(defaultModel.model_id);
        }
      }
    }
  }, [availableModels, selectedModelId]);

  // Restore state on component mount
  React.useEffect(() => {
    console.log('🔍 OneClickCreator: Checking for tasks on mount...');

    // Check for running tasks and restore state
    const runningTasks = getTasksByPage('one-click-creator');
    console.log('🔍 Found running tasks:', runningTasks);

    if (runningTasks.length > 0) {
      const task = runningTasks[0];
      console.log('🔍 Processing task:', task);

      if (task.status === 'processing' || task.status === 'pending') {
        console.log('✅ Restoring processing task state');

        // Restore form state FIRST to set the correct creation mode
        restoreFormState(task.formData);
        setIsCreating(true);
        setCurrentStep('create');

        console.log('✅ Task restoration complete for creation mode:', task.formData?.creationMode);
        return; // Don't load cached results if there's an active task
      }
    }

    // Only load cached results if user hasn't explicitly started fresh AND user hasn't made changes
    // On page refresh, we DO want to restore completed results
    if (!startFresh && !uploadedFile && !customMessage && !uploadedMediaId) {
      const recentTask = getRecentCompletedTask('one-click-creator');
      console.log('🔍 Recent One-Click Creator task on page load:', recentTask);
      if (recentTask && recentTask.results) {
        console.log('✅ Loading cached One-Click Creator results:', recentTask.results);
        setPostsResult(recentTask.results);
        setCurrentStep('complete');
        restoreFormState(recentTask.formData);
      } else if (recentTask) {
        console.log('❌ One-Click Creator task found but no results field');
      }
    } else if (uploadedFile || customMessage || uploadedMediaId) {
      console.log('🔄 User has made changes - not loading cached results');
    }
    
    // Reset page refresh flag after initial load
    if (isPageRefresh) {
      console.log('🔄 Page refresh detected - will reset flag after initial load');
      setTimeout(() => setIsPageRefresh(false), 100);
    }
  }, [getTasksByPage, getRecentCompletedTask, startFresh]);

  // Monitor task status from context
  React.useEffect(() => {
    const runningTasks = getTasksByPage('one-click-creator');
    const isRunning = isTaskRunning('one-click-creator');

    // Check if any task just completed
    const completedTask = runningTasks.find(task => task.status === 'completed');
    if (completedTask && isCreating && completedTask.results) {
      console.log('✅ One-Click Creator task completed:', completedTask.results);

      // Only show toast if we haven't already notified for this task
      if (!notifiedTaskIds.current.has(completedTask.id)) {
        notifiedTaskIds.current.add(completedTask.id);

        toast({
          title: "🎉 Posts Created!",
          description: "Your posts have been created successfully!",
        });
      }

      setPostsResult(completedTask.results);
      setIsCreating(false);
      setCurrentStep('complete');
    } else if (isRunning !== isCreating) {
      setIsCreating(isRunning);
      if (isRunning) {
        setCurrentStep('create');
        // Only clear previous results when starting a NEW generation (not on page refresh)
        if (!isPageRefresh) {
          setPostsResult(null);
          setBrandAnalysis(null);
        }
      }
    }
  }, [getTasksByPage, isTaskRunning, isCreating, isPageRefresh]);

  const restoreFormState = (formData: any) => {
    if (!formData) return;

    console.log('🔄 Restoring One-Click Creator form state:', formData);

    if (formData.customMessage !== undefined) {
      setCustomMessage(formData.customMessage);
      console.log('✅ Restored customMessage:', formData.customMessage);
    }
    if (formData.scheduleImmediately !== undefined) {
      setScheduleImmediately(formData.scheduleImmediately);
      console.log('✅ Restored scheduleImmediately:', formData.scheduleImmediately);
    }
    if (formData.creationMode !== undefined) {
      setCreationMode(formData.creationMode);
      console.log('✅ Restored creationMode:', formData.creationMode);
    }
    if (formData.uploadedMediaId !== undefined) {
      setUploadedMediaId(formData.uploadedMediaId);
      console.log('✅ Restored uploadedMediaId:', formData.uploadedMediaId);
    }
    if (formData.brandAnalysis !== undefined) {
      setBrandAnalysis(formData.brandAnalysis);
      console.log('✅ Restored brandAnalysis:', formData.brandAnalysis);
    }
    if (formData.selectedPlatforms !== undefined) {
      setSelectedPlatforms(formData.selectedPlatforms);
      console.log('✅ Restored selectedPlatforms:', formData.selectedPlatforms);
    }
  };

  // File upload handling
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file || !currentClient) return;

    setUploadedFile(file);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', 'One-Click Creator Upload');
      formData.append('description', 'Image for one-click campaign creation');

      const response = await fetch(`${getApiUrl()}/api/v1/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      setUploadedMediaId(result.file.id);
      setCurrentStep('analyze');

      // Auto-analyze the brand and image
      await analyzeBrandAndImage(result.file.id);

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  }, [currentClient, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1,
    disabled: isUploading || !currentClient || hasConnectedAccounts === false
  });

  // Brand analysis
  const analyzeBrandAndImage = async (mediaId: string) => {
    if (!currentClient) return;

    setIsAnalyzing(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/one-click-creator/analyze-brand/${mediaId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-Client-ID': currentClient.id,
        },
      });

      if (!response.ok) {
        throw new Error('Analysis failed');
      }

      const analysis = await response.json();
      setBrandAnalysis(analysis);

      toast({
        title: "Analysis Complete",
        description: "Your brand and image have been analyzed. Ready to create your posts!",
      });

    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to analyze your brand and image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Brand-only analysis (for brand mode)
  const analyzeBrandOnly = async (serviceDescription?: string) => {
    if (!currentClient) return null;

    console.log('🔍 Starting brand-only analysis with service description:', serviceDescription);
    setIsAnalyzing(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/one-click-creator/analyze-brand-only`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify({
          service_description: serviceDescription || null,
        }),
      });

      console.log('🔍 Brand analysis response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('🔍 Brand analysis error response:', errorData);
        throw new Error(errorData.detail || 'Brand analysis failed');
      }

      const analysis = await response.json();
      console.log('🔍 Brand analysis result:', analysis);
      setBrandAnalysis(analysis);

      // No toast notification - this happens silently in the background
      return analysis; // Return the analysis result

    } catch (error) {
      console.error('Brand analysis error:', error);
      toast({
        title: "Brand Analysis Failed",
        description: "Failed to analyze your brand information. Please try again.",
        variant: "destructive",
      });
      throw error; // Re-throw to stop the creation process
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Posts creation
  const createPosts = async () => {
    if (!currentClient) return;

    // Check for connected social media accounts first
    if (hasConnectedAccounts === false) {
      toast({
        title: "No Social Media Accounts Connected",
        description: "Please connect at least one social media account to create posts.",
        variant: "destructive",
      });
      return;
    }

    // Check if any platforms are selected
    if (selectedPlatforms.length === 0) {
      toast({
        title: "No Platforms Selected",
        description: "Please select at least one platform to create posts for.",
        variant: "destructive",
      });
      return;
    }

    // For image mode, we need uploadedMediaId. For brand mode, we don't.
    if (creationMode === 'image' && !uploadedMediaId) return;

    setIsCreating(true);
    setCurrentStep('create');
    setCreationProgress(0);
    
    // Clear previous results to prevent glitching during new generation
    // (only when user manually starts generation, not on page refresh)
    setPostsResult(null);
    setBrandAnalysis(null);
    
    // Mark that this is not a page refresh anymore
    setIsPageRefresh(false);

    // Simulate progress during creation with more realistic pacing
    const progressInterval = setInterval(() => {
      setCreationProgress(prev => {
        // Slow down as we approach completion
        if (prev >= 85) {
          return Math.min(prev + Math.random() * 2, 92); // Very slow increment, stop at 92%
        } else if (prev >= 70) {
          return prev + Math.random() * 4; // Slower increment
        } else if (prev >= 50) {
          return prev + Math.random() * 6; // Medium increment
        } else {
          return prev + Math.random() * 8; // Faster at the beginning
        }
      });
    }, 800); // Slower interval (800ms instead of 500ms)

    // Create a temporary task ID for tracking during processing
    const tempTaskId = `one-click-temp-${Date.now()}`;
    console.log('💾 Storing temporary One-Click Creator task for processing:', tempTaskId);

    try {
      // For brand mode, if we don't have brand analysis yet, do it first (silently)
      let currentBrandAnalysis = brandAnalysis;
      if (creationMode === 'brand' && !currentBrandAnalysis) {
        console.log('🔍 Brand mode: Running brand-only analysis first...');
        try {
          currentBrandAnalysis = await analyzeBrandOnly(customMessage);
          if (!currentBrandAnalysis) {
            console.error('❌ Brand analysis returned null');
            throw new Error('Brand analysis failed to return results');
          }
        } catch (analysisError) {
          console.error('❌ Brand analysis failed:', analysisError);
          throw new Error('Brand analysis failed');
        }
      }

      // Store comprehensive form data for restoration (after brand analysis)
      const comprehensiveFormData = {
        customMessage,
        scheduleImmediately,
        creationMode,
        uploadedMediaId,
        brandAnalysis: currentBrandAnalysis,
        selectedPlatforms,
      };

      // Store task immediately for persistence during processing
      addTask({
        id: tempTaskId,
        task_type: 'one-click-creator',
        status: 'processing',
        created_at: new Date().toISOString(),
        page: 'one-click-creator',
        formData: comprehensiveFormData,
      });

      const response = await fetch(`${getApiUrl()}/api/v1/one-click-creator/create-campaign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
          'X-Client-ID': currentClient.id,
        },
        body: JSON.stringify({
          input_media_file_id: creationMode === 'image' ? uploadedMediaId : null,
          custom_message: customMessage || null,
          model_id: selectedModelId || null,
          schedule_immediately: scheduleImmediately,
          creation_mode: creationMode,
          post_description: creationMode === 'brand' ? customMessage : null,
          target_platforms: selectedPlatforms, // Use selected platforms instead of letting backend decide
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Posts creation failed');
      }

      const result = await response.json();
      console.log('🎯 One-Click Creator API response:', result);
      console.log('🔍 Response keys:', Object.keys(result));
      console.log('🖼️ Generated posts:', result.generated_posts);
      console.log('🖼️ First post media_file_id:', result.generated_posts?.[0]?.media_file_id);

      // Complete the progress
      clearInterval(progressInterval);
      setCreationProgress(100);

      setPostsResult(result);

      // Update brand analysis with real data from backend
      if (result.brand_analysis) {
        setBrandAnalysis(result.brand_analysis);
      }

      // Update the existing task with results
      const finalTaskId = result.task_id || tempTaskId;
      console.log('✅ Updating One-Click Creator task with results:', finalTaskId);

      updateTask(tempTaskId, {
        id: finalTaskId,
        status: 'completed',
        completed_at: new Date().toISOString(),
        results: result,
      });

      setCurrentStep('complete');

      // Refresh user data to update credits after successful campaign creation
      try {
        await refreshUser();
        console.log('✅ User credits refreshed after one-click campaign creation');
      } catch (refreshError) {
        console.warn('Failed to refresh user data after campaign creation:', refreshError);
      }

      toast({
        title: "🎉 Posts Created!",
        description: result.message,
      });

    } catch (error) {
      console.error('Posts creation error:', error);

      // Clear progress interval on error
      clearInterval(progressInterval);
      setCreationProgress(0);

      // Update task status to failed
      updateTask(tempTaskId, {
        status: 'failed',
        completed_at: new Date().toISOString(),
      });

      toast({
        title: "Posts Creation Failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
      setCurrentStep(creationMode === 'brand' ? 'upload' : 'analyze');
    } finally {
      setIsCreating(false);
    }
  };

  const resetFlow = () => {
    setUploadedFile(null);
    setUploadedMediaId(null);
    setCustomMessage('');
    setBrandAnalysis(null);
    setPostsResult(null);
    setCurrentStep('upload');
    setIsCreating(false);
    setIsAnalyzing(false);
    setIsUploading(false);
    setCreationProgress(0);
    setCreationMode('image'); // Reset to default mode
    setSelectedPlatforms([]); // Reset selected platforms
    setStartFresh(true); // Mark that user wants to start fresh
    setIsPageRefresh(false); // Reset page refresh flag
    notifiedTaskIds.current.clear(); // Clear notified tasks to allow new notifications
    console.log('🔄 Reset flow completed - all state cleared');
  };

  const startNewCreation = () => {
    // Clear all cached results and running tasks
    const allTasks = getTasksByPage('one-click-creator');
    allTasks.forEach(task => {
      console.log('🗑️ Removing task:', task.id);
      removeTask(task.id);
    });

    resetFlow();
    console.log('🆕 Started completely fresh creation');
  };

  // Nuclear reset function to clear everything and fix stuck states
  const performNuclearReset = useCallback(() => {
    console.log('💥 NUCLEAR RESET: Clearing all One-Click Creator states and stuck tasks');

    try {
      // 1. Remove all active tasks for this page to clear stuck processing states
      const allTasks = getTasksByPage('one-click-creator');
      allTasks.forEach(task => {
        console.log('🗑️ Removing stuck task:', task.id, task.status);
        removeTask(task.id);
      });

      // 2. Clear any localStorage keys that might cause issues
      const keysToRemove = [
        'oneClickCreator_completedTaskIds',
        'oneClickCreator_generationProgress',
        'oneClickCreator_generationStartTime',
        'oneClickCreator_lastResults',
        'oneClickCreator_sessionState',
        'oneClickCreator_brandAnalysis',
        'oneClickCreator_postsResult'
      ];

      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
          // Also try with user/client prefixes
          if (user?.id && currentClient?.id) {
            localStorage.removeItem(`${key}_${user.id}_${currentClient.id}`);
          }
        } catch (error) {
          console.warn(`Failed to remove ${key}:`, error);
        }
      });

      // 3. Reset all component state
      setUploadedFile(null);
      setUploadedMediaId(null);
      setSelectedMediaFromLibrary([]);
      setCustomMessage('');
      setSelectedModelId('');
      setScheduleImmediately(false);
      setIsUploading(false);
      setIsAnalyzing(false);
      setIsCreating(false);
      setCreationProgress(0);
      setBrandAnalysis(null);
      setPostsResult(null);
      setCurrentStep('upload');
      setCreationMode('image');
      setShowUpgradeModal(false);
      setStartFresh(true);

      // 4. Clear notification tracking
      notifiedTaskIds.current.clear();

      console.log('✅ Nuclear reset completed successfully');

      toast({
        title: "🚀 Complete Reset Successful",
        description: "All states cleared. You can now start from a clean slate.",
      });

    } catch (error) {
      console.error('❌ Nuclear reset failed:', error);
      toast({
        title: "Reset Error",
        description: "Some states may not have been cleared. Try refreshing the page.",
        variant: "destructive",
      });
    }
  }, [getTasksByPage, removeTask, user?.id, currentClient?.id, toast]);

  // Helper functions for platform display
  const getPlatformIcon = (platform: string) => {
    const iconMap: { [key: string]: React.ElementType } = {
      instagram: Instagram,
      facebook: Facebook,
      linkedin: Linkedin,
      twitter: Twitter,
      x: Twitter,
      tiktok: MessageSquare
    };
    
    const IconComponent = iconMap[platform.toLowerCase()];
    return IconComponent ? <IconComponent className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />;
  };

  const formatPlatformName = (platform: string) => {
    const names = {
      instagram: 'Instagram',
      facebook: 'Facebook',
      linkedin: 'LinkedIn',
      twitter: 'X',
      tiktok: 'TikTok'
    };
    return names[platform] || platform;
  };

  if (!currentClient) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Client Selected</h3>
            <p className="text-muted-foreground mb-4">Please select a client to use One-Click Creator.</p>
            <Button onClick={() => navigate('/app/dashboard')}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ComingSoonWrapper
      feature="one_click_creator"
      title="Upgrade to Unlock One-Click Creator"
      description="Turn one image into a complete social media campaign with a single click. Upgrade to access this powerful feature."
      features={[
        "AI-powered brand analysis",
        "Automatic post generation for multiple platforms",
        "Smart scheduling recommendations",
        "Instant campaign creation"
      ]}

    >
      <div className="min-h-screen bg-background text-foreground">
        {/* Enhanced Header */}
        <div className="relative backdrop-blur-2xl bg-white/80 dark:bg-slate-900/80 border-b border-slate-200/50 dark:border-slate-700/30 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
          <div className="relative px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                  <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-black bg-gradient-to-r from-slate-900 via-blue-600 to-purple-600 dark:from-white dark:via-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                    One-Click Creator
                  </h1>
                  <p className="text-slate-600 dark:text-slate-300 text-lg mt-1">
                    Transform ideas into professional social media posts in seconds
                  </p>

                </div>
              </div>
              {!isMobile && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      performNuclearReset();
                    }}
                    className="px-3 py-2 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-900/30 border border-red-200 dark:border-red-800 hover:from-red-100 hover:to-red-200 dark:hover:from-red-900/30 dark:hover:to-red-900/40 text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200 transition-all duration-200 rounded text-sm flex items-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4" />
                    <span className="hidden sm:inline font-medium">Reset</span>
                  </button>
                  <Button
                    variant="outline"
                    onClick={() => restartTour(oneClickCreatorSteps, 'oneClickCreator')}
                    className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border-slate-300/50 dark:border-slate-600/50 hover:bg-slate-100/50 dark:hover:bg-slate-700/50"
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Take Tour
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>



        {/* Cached Results Notification */}
        {currentStep === 'complete' && postsResult && !startFresh && !isCreating && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-blue-200/50 dark:border-blue-700/30"
          >
            <div className="px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-500/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      You have recent posts from your last session
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      Continue viewing your results or start a new creation
                    </p>
                  </div>
                </div>
                <Button
                  onClick={startNewCreation}
                  variant="outline"
                  size="sm"
                  className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  Start Fresh
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* No Social Media Accounts Warning */}
        {isCheckingConnections ? (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b border-blue-200/50 dark:border-blue-700/30"
          >
            <div className="px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500/10 rounded-full flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Checking Social Media Connections...
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-400">
                    Please wait while we verify your connected accounts
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        ) : hasConnectedAccounts === false && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-b border-amber-200/50 dark:border-amber-700/30"
          >
            <div className="px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-amber-500/10 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      No Social Media Accounts Connected
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      Connect your social media accounts to create and publish posts. You can still create drafts without connections.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/app/settings/social-accounts')}
                  variant="outline"
                  size="sm"
                  className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Connect Accounts
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Content */}
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

            {/* Left Panel - Controls */}
            <div className="lg:col-span-5 space-y-4">

              {/* Compact Tips Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden rounded-xl bg-gradient-to-br from-white via-blue-50/50 to-purple-50/50 dark:from-slate-800 dark:via-slate-800/80 dark:to-slate-900/80 border border-blue-200/50 dark:border-slate-700/50 shadow-lg backdrop-blur-xl"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
                <div className="relative p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Pro Tips</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-xs">
                      <Target className="w-3 h-3 text-blue-500 flex-shrink-0" />
                      <span className="text-slate-600 dark:text-slate-400">Complete your </span>
                      <button
                        onClick={() => navigate(`/app/clients/${currentClient.id}/brand-kit`)}
                        className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline underline-offset-1 transition-colors"
                      >
                        Brand Kit
                      </button>
                      <span className="text-slate-600 dark:text-slate-400">for better results</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs">
                      <Wand2 className="w-3 h-3 text-purple-500 flex-shrink-0" />
                      <span className="text-slate-600 dark:text-slate-400">Try </span>
                      <button
                        onClick={() => navigate('/app/content-creation/ad-studio')}
                        className="font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 underline underline-offset-1 transition-colors"
                      >
                        Image Studio
                      </button>
                      <span className="text-slate-600 dark:text-slate-400">for more generation options</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Compact Progress Steps */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="relative overflow-hidden rounded-xl bg-gradient-to-br from-white/90 via-blue-50/80 to-purple-50/70 dark:from-card/80 dark:to-card/40 backdrop-blur-xl border border-blue-200/50 dark:border-border/30 shadow-lg shadow-blue-500/10 dark:shadow-primary/5"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
                <div className="relative p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Progress</h3>
                    <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-full">
                      {['upload', 'analyze', 'create', 'complete'].indexOf(currentStep) + 1}/4
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    {(creationMode === 'image' ? [
                      { key: 'upload', icon: Upload, label: 'Upload', color: 'blue' },
                      { key: 'analyze', icon: Eye, label: 'Analyze', color: 'purple' },
                      { key: 'create', icon: Wand2, label: 'Create', color: 'pink' },
                      { key: 'complete', icon: CheckCircle, label: 'Complete', color: 'green' }
                    ] : [
                      { key: 'upload', icon: MessageSquare, label: 'Setup', color: 'blue' },
                      { key: 'analyze', icon: Target, label: 'Generate', color: 'purple' },
                      { key: 'create', icon: Wand2, label: 'Create', color: 'pink' },
                      { key: 'complete', icon: CheckCircle, label: 'Complete', color: 'green' }
                    ]).map((step, index) => {
                      const stepIndex = ['upload', 'analyze', 'create', 'complete'].indexOf(currentStep);
                      const isActive = currentStep === step.key;
                      const isCompleted = index < stepIndex;
                      const colorClasses = {
                        blue: 'from-blue-500 to-blue-600',
                        purple: 'from-purple-500 to-purple-600',
                        pink: 'from-pink-500 to-pink-600',
                        green: 'from-green-500 to-green-600'
                      };

                      return (
                        <div key={step.key} className="flex flex-col items-center space-y-1.5 relative">
                          <motion.div
                            className={`relative flex items-center justify-center w-8 h-8 rounded-xl transition-all duration-500 ${isActive
                              ? `bg-gradient-to-br ${colorClasses[step.color]} text-white shadow-lg shadow-${step.color}-500/30`
                              : isCompleted
                                ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/30'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-600'
                              }`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <step.icon className="w-4 h-4" />
                            {isActive && (
                              <motion.div
                                className="absolute inset-0 rounded-xl border border-white/50"
                                animate={{ scale: [1, 1.1, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              />
                            )}
                          </motion.div>
                          <span className={`text-xs font-medium transition-colors ${isActive ? `text-${step.color}-600 dark:text-${step.color}-400` :
                            isCompleted ? 'text-green-600 dark:text-green-400' :
                              'text-slate-500 dark:text-slate-400'
                            }`}>
                            {step.label}
                          </span>

                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>

              <AnimatePresence mode="wait">
                {/* Step 1: Upload Image */}
                {currentStep === 'upload' && (
                  <motion.div
                    key="upload"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/90 via-purple-50/80 to-pink-50/70 dark:from-card/80 dark:to-card/40 backdrop-blur-xl border border-purple-200/50 dark:border-border/30 shadow-lg shadow-purple-500/10 dark:shadow-purple-500/5"
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>
                      <div className="relative p-5">
                        <div className="space-y-5">
                          {/* Enhanced Creation Mode Toggle */}
                          <div className="relative">
                            <div className="flex items-center justify-center space-x-2 bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-2xl p-2 shadow-inner border border-slate-300/50 dark:border-slate-600/30">
                              <motion.button
                                onClick={() => setCreationMode('image')}
                                className={`relative flex-1 flex items-center justify-center space-x-3 px-4 py-4 rounded-xl font-semibold text-sm transition-all duration-300 ${creationMode === 'image'
                                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xl shadow-indigo-500/10 dark:shadow-indigo-500/20 border border-indigo-200/50 dark:border-indigo-700/50'
                                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-800/50'
                                  }`}
                                whileHover={{ scale: creationMode !== 'image' ? 1.02 : 1 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${creationMode === 'image'
                                  ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                                  : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                                  }`}>
                                  <Upload className="w-4 h-4" />
                                </div>
                                <div className="text-left">
                                  <div className="font-bold text-sm">With Image</div>
                                  <div className={`text-xs ${creationMode === 'image'
                                    ? 'text-slate-600 dark:text-slate-400'
                                    : 'text-slate-500 dark:text-slate-500'
                                    }`}>
                                    Create with visuals
                                  </div>
                                </div>
                                {creationMode === 'image' && (
                                  <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-r from-green-400 to-green-500 rounded-full border-2 border-white dark:border-slate-900 shadow-lg"
                                  >
                                    <div className="w-full h-full rounded-full bg-green-500 animate-ping"></div>
                                  </motion.div>
                                )}
                              </motion.button>

                              <motion.button
                                onClick={() => setCreationMode('brand')}
                                className={`relative flex-1 flex items-center justify-center space-x-3 px-4 py-4 rounded-xl font-semibold text-sm transition-all duration-300 ${creationMode === 'brand'
                                  ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xl shadow-indigo-500/10 dark:shadow-indigo-500/20 border border-indigo-200/50 dark:border-indigo-700/50'
                                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-800/50'
                                  }`}
                                whileHover={{ scale: creationMode !== 'brand' ? 1.02 : 1 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300 ${creationMode === 'brand'
                                  ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                                  : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'
                                  }`}>
                                  <Palette className="w-4 h-4" />
                                </div>
                                <div className="text-left">
                                  <div className="font-bold text-sm">Brand Only</div>
                                  <div className={`text-xs ${creationMode === 'brand'
                                    ? 'text-slate-600 dark:text-slate-400'
                                    : 'text-slate-500 dark:text-slate-500'
                                    }`}>
                                    Generate from brandkit
                                  </div>
                                </div>
                                {creationMode === 'brand' && (
                                  <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-r from-green-400 to-green-500 rounded-full border-2 border-white dark:border-slate-900 shadow-lg"
                                  >
                                    <div className="w-full h-full rounded-full bg-green-500 animate-ping"></div>
                                  </motion.div>
                                )}
                              </motion.button>
                            </div>
                          </div>

                          {/* Compact Title and Description */}
                          <div className="text-center space-y-3">
                            <motion.div
                              key={creationMode}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="space-y-2"
                            >
                              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                {creationMode === 'image' ? 'Upload & Create' : 'Brand-Powered Posts'}
                              </h3>
                              <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="text-slate-500 dark:text-slate-500 text-sm max-w-md mx-auto"
                              >
                                {creationMode === 'image'
                                  ? 'Turn any image into branded social content'
                                  : 'Generate posts using your brand information'
                                }
                              </motion.p>
                            </motion.div>

                            {/* Feature highlights */}
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.2 }}
                              className="flex items-center justify-center space-x-4 text-xs"
                            >

                            </motion.div>
                          </div>
                        </div>
                      </div>
                      <div className="relative px-6 pb-6">
                        {creationMode === 'image' ? (
                          <div className="space-y-3" data-tour="upload-section">
                            {/* Media Library Selector with Access Control */}
                            {isOneClickLocked ? (
                              <motion.div
                                onClick={() => setShowUpgradeModal(true)}
                                className="relative overflow-hidden rounded-xl cursor-pointer transition-all duration-300 group hover:scale-[1.01] hover:shadow-md"
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                              >
                                {/* Hover gradient background */}
                                <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

                                {/* Main background */}
                                <div className="relative backdrop-blur-xl bg-white/80 dark:bg-slate-800/40 border border-slate-300/50 dark:border-slate-600/30 hover:border-indigo-500/50 rounded-2xl shadow-sm p-8 transition-all duration-300">
                                  <motion.div
                                    className="flex flex-col items-center space-y-4"
                                    whileHover={{ y: -2 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    <div className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl shadow-lg">
                                      <Crown className="w-5 h-5 text-yellow-300" />
                                      <span className="text-white font-semibold">Select from Library or Upload</span>
                                    </div>
                                    
                                    {/* Format indicators */}
                                    <div className="flex items-center space-x-2">
                                      {['JPG', 'PNG', 'WEBP'].map((format) => (
                                        <span
                                          key={format}
                                          className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-md font-medium"
                                        >
                                          {format}
                                        </span>
                                      ))}
                                    </div>
                                    
                                    <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
                                      Upgrade to access media library and upload
                                    </p>
                                  </motion.div>
                                </div>
                              </motion.div>
                            ) : hasConnectedAccounts === false ? (
                              // Disabled state when no social accounts are connected
                              <motion.div
                                className="relative overflow-hidden rounded-xl opacity-50 cursor-not-allowed"
                              >
                                <div className="relative backdrop-blur-xl bg-white/80 dark:bg-slate-800/40 border border-slate-300/50 dark:border-slate-600/30 rounded-2xl shadow-sm p-8">
                                  <motion.div className="flex flex-col items-center space-y-4">
                                    <div className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-xl shadow-lg">
                                      <AlertCircle className="w-5 h-5 text-white" />
                                      <span className="text-white font-semibold">Connect Social Accounts First</span>
                                    </div>
                                    
                                    {/* Format indicators */}
                                    <div className="flex items-center space-x-2">
                                      {['JPG', 'PNG', 'WEBP'].map((format) => (
                                        <span
                                          key={format}
                                          className="text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-md font-medium"
                                        >
                                          {format}
                                        </span>
                                      ))}
                                    </div>
                                    
                                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
                                      Connect your social media accounts to upload images and create posts
                                    </p>
                                    
                                    <Button
                                      onClick={() => navigate('/app/settings/social-accounts')}
                                      variant="outline"
                                      size="sm"
                                      className="mt-2 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                    >
                                      <Share2 className="w-4 h-4 mr-2" />
                                      Connect Accounts
                                    </Button>
                                  </motion.div>
                                </div>
                              </motion.div>
                            ) : (
                              <MultiMediaFileSelector
                                selectedFiles={selectedMediaFromLibrary}
                                onSelectionChange={(files) => {
                                  setSelectedMediaFromLibrary(files);
                                  if (files.length > 0) {
                                    setUploadedMediaId(files[0].id);
                                    setUploadedFile(null);
                                  }
                                }}
                                onDone={async (files) => {
                                  if (files.length > 0) {
                                    const mediaId = files[0].id;
                                    setUploadedMediaId(mediaId);
                                    setCurrentStep('analyze');
                                    await analyzeBrandAndImage(mediaId);
                                  }
                                }}
                                acceptedTypes={['image']}
                                placeholder="Select from library or upload"
                                theme="brand"
                                maxFiles={1}
                              />
                            )}
                          </div>
                        ) : (
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="space-y-6"
                          >


                            {/* Minimalist Service Input */}
                            <div className="space-y-4">
                              <Input
                                id="post-description"
                                placeholder="Describe your post (optional)"
                                value={customMessage}
                                onChange={(e) => setCustomMessage(e.target.value)}
                                className="text-base p-4 rounded-xl bg-background/50 border border-white/20 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all placeholder-muted-foreground text-foreground backdrop-blur-sm"
                              />

                              {/* Simple Schedule Toggle */}
                              <div className="flex items-center justify-between">
                                <Label htmlFor="schedule-immediately-brand" className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                                  Schedule immediately
                                </Label>
                                <Switch
                                  id="schedule-immediately-brand"
                                  checked={scheduleImmediately}
                                  onCheckedChange={setScheduleImmediately}
                                  className="data-[state=checked]:bg-purple-500"
                                />
                              </div>

                              {/* Brand Kit Tip */}
                              {!currentClient?.brand_voice && (
                                <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                  <div className="flex items-center space-x-2 text-amber-700 dark:text-amber-300">
                                    <Target className="w-4 h-4" />
                                    <span className="text-sm font-medium">Get Better Results</span>
                                  </div>
                                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                    Complete your{' '}
                                    <button
                                      onClick={() => window.open(`/app/clients/${currentClient?.id}/brand-kit`, '_blank')}
                                      className="underline hover:text-amber-800 dark:hover:text-amber-200 font-medium"
                                    >
                                      Brand Kit
                                    </button>
                                    {' '}for personalized captions and hashtags
                                  </p>
                                </div>
                              )}

                              {/* No Social Accounts Warning for Brand Mode */}
                              {hasConnectedAccounts === false && (
                                <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                  <div className="flex items-center space-x-2 text-amber-700 dark:text-amber-300">
                                    <AlertCircle className="w-4 h-4" />
                                    <span className="text-sm font-medium">No Social Accounts Connected</span>
                                  </div>
                                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                    Connect your social media accounts to create and publish posts.{' '}
                                    <button
                                      onClick={() => navigate('/app/settings/social-accounts')}
                                      className="underline hover:text-amber-800 dark:hover:text-amber-200 font-medium"
                                    >
                                      Connect accounts
                                    </button>
                                  </p>
                                </div>
                              )}

                              {/* Platform Selection for Brand Mode */}
                              {hasConnectedAccounts && connectedPlatforms.length > 0 && (
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Select Platforms</Label>
                                  <div className="flex flex-wrap gap-2">
                                    {connectedPlatforms.map((platform) => {
                                      const isSelected = selectedPlatforms.includes(platform);
                                      return (
                                        <button
                                          key={platform}
                                          onClick={() => {
                                            if (isSelected) {
                                              // Deselect platform (but ensure at least one remains selected)
                                              if (selectedPlatforms.length > 1) {
                                                setSelectedPlatforms(prev => prev.filter(p => p !== platform));
                                              } else {
                                                toast({
                                                  title: "At least one platform required",
                                                  description: "You must select at least one platform to create posts for.",
                                                  variant: "destructive",
                                                });
                                              }
                                            } else {
                                              // Select platform
                                              setSelectedPlatforms(prev => [...prev, platform]);
                                            }
                                          }}
                                          className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                                            isSelected
                                              ? 'bg-primary text-primary-foreground shadow-md'
                                              : 'bg-muted text-muted-foreground hover:bg-muted/80 border border-border'
                                          }`}
                                        >
                                          {getPlatformIcon(platform)}
                                          <span>{formatPlatformName(platform)}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <p className="text-xs text-slate-600 dark:text-slate-400">
                                    {selectedPlatforms.length} of {connectedPlatforms.length} platforms selected
                                  </p>
                                </div>
                              )}

                              {/* Clean Create Button */}
                              <button
                                onClick={async () => {
                                  if (isOneClickLocked) {
                                    setShowUpgradeModal(true);
                                  } else {
                                    await createPosts();
                                  }
                                }}
                                disabled={isCreating || hasConnectedAccounts === false || selectedPlatforms.length === 0}
                                className="w-full h-12 flex items-center justify-center space-x-2 text-white text-base font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                  background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                                  backgroundSize: '200% 200%',
                                  backgroundPosition: 'left center',
                                  boxShadow: '0 10px 25px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isCreating) {
                                    e.currentTarget.style.backgroundPosition = 'right center';
                                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(48, 179, 221, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundPosition = 'left center';
                                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
                                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                }}
                              >
                                {isCreating ? (
                                  <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Creating...</span>
                                  </>
                                ) : hasConnectedAccounts === false ? (
                                  <>
                                    <AlertCircle className="w-4 h-4" />
                                    <span>Connect Accounts First</span>
                                  </>
                                ) : selectedPlatforms.length === 0 ? (
                                  <>
                                    <Target className="w-4 h-4" />
                                    <span>Select Platforms</span>
                                  </>
                                ) : (
                                  <>
                                    {isOneClickLocked && <Crown className="w-4 h-4 text-yellow-300" />}
                                    {!isOneClickLocked && <Wand2 className="w-4 h-4" />}
                                    <span>Create Posts</span>
                                    {!isOneClickLocked && (
                                      <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
                                        {getTaskCost('One-Click Creator')} credits
                                      </span>
                                    )}
                                  </>
                                )}
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  </motion.div>
                )}

                {/* Step 2: Brand Analysis & Customization */}
                {(currentStep === 'analyze' || currentStep === 'create') && brandAnalysis && (
                  <motion.div
                    key="analyze"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-4"
                  >

                    {/* Brand Analysis Results */}
                    <Card className="relative overflow-hidden bg-card/50 backdrop-blur-xl border border-border/50">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>
                      <CardHeader className="relative">
                        <CardTitle className="flex items-center space-x-2">
                          <Sparkles className="w-5 h-5" />
                          <span>Smart Analysis</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="relative space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <Palette className="w-3 h-3 text-primary" />
                              <span className="font-medium">Style</span>
                            </div>
                            <p className="text-muted-foreground capitalize text-xs">
                              {brandAnalysis.visual_style.replace('-', ' ')}
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <Target className="w-3 h-3 text-primary" />
                              <span className="font-medium">Type</span>
                            </div>
                            <p className="text-muted-foreground capitalize text-xs">
                              {brandAnalysis.ad_intent.replace('-', ' ')}
                            </p>
                          </div>
                        </div>

                        <div data-tour="platform-selection">
                          <div className="flex items-center space-x-2 mb-2">
                            <Share2 className="w-3 h-3 text-primary" />
                            <span className="font-medium text-sm">Recommended Platforms</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {(() => {
                              console.log('🔍 FRONTEND: Platforms being displayed:', brandAnalysis.optimal_platforms);
                              return brandAnalysis.optimal_platforms;
                            })().map((platform) => (
                              <div
                                key={platform}
                                className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded-full text-xs"
                              >
                                {getPlatformIcon(platform)}
                                <span>{formatPlatformName(platform)}</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Warning when no accounts are connected */}
                          {!brandAnalysis.has_connected_accounts && (
                            <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                              <div className="flex items-start space-x-2">
                                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-amber-900 dark:text-amber-100">
                                    No social accounts connected
                                  </p>
                                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                    Connect your social media accounts to publish posts. You can still create drafts.
                                  </p>
                                  <button
                                    onClick={() => navigate('/app/settings/social-accounts')}
                                    className="mt-2 text-xs font-medium text-amber-800 dark:text-amber-200 hover:text-amber-900 dark:hover:text-amber-100 underline"
                                  >
                                    Connect accounts →
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Info when some platforms are not connected */}
                          {brandAnalysis.has_connected_accounts && !brandAnalysis.all_platforms_connected && (
                            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                              <div className="flex items-start space-x-2">
                                <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-blue-900 dark:text-blue-100">
                                    Some platforms not connected
                                  </p>
                                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                                    Connect more accounts to post to all recommended platforms.
                                  </p>
                                  <button
                                    onClick={() => navigate('/app/settings/social-accounts')}
                                    className="mt-2 text-xs font-medium text-blue-800 dark:text-blue-200 hover:text-blue-900 dark:hover:text-blue-100 underline"
                                  >
                                    Connect more accounts →
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Customization Options */}
                    {currentStep === 'analyze' && (
                      <Card className="relative overflow-hidden bg-card/50 backdrop-blur-xl border border-border/50">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>
                        <CardHeader className="relative">
                          <CardTitle className="flex items-center space-x-2">
                            <MessageSquare className="w-5 h-5" />
                            <span>Customize</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="relative space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="custom-message" className="text-sm">Custom Message</Label>
                            <Input
                              id="custom-message"
                              placeholder="e.g., Check out our latest product!"
                              value={customMessage}
                              onChange={(e) => setCustomMessage(e.target.value)}
                              className="text-sm"
                            />
                          </div>

                          {/* Platform Selection */}
                          {hasConnectedAccounts && connectedPlatforms.length > 0 && (
                            <div className="space-y-2">
                              <Label className="text-sm">Select Platforms</Label>
                              <div className="flex flex-wrap gap-2">
                                {connectedPlatforms.map((platform) => {
                                  const isSelected = selectedPlatforms.includes(platform);
                                  return (
                                    <button
                                      key={platform}
                                      onClick={() => {
                                        if (isSelected) {
                                          // Deselect platform (but ensure at least one remains selected)
                                          if (selectedPlatforms.length > 1) {
                                            setSelectedPlatforms(prev => prev.filter(p => p !== platform));
                                          } else {
                                            toast({
                                              title: "At least one platform required",
                                              description: "You must select at least one platform to create posts for.",
                                              variant: "destructive",
                                            });
                                          }
                                        } else {
                                          // Select platform
                                          setSelectedPlatforms(prev => [...prev, platform]);
                                        }
                                      }}
                                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 ${
                                        isSelected
                                          ? 'bg-primary text-primary-foreground shadow-md'
                                          : 'bg-muted text-muted-foreground hover:bg-muted/80 border border-border'
                                      }`}
                                    >
                                      {getPlatformIcon(platform)}
                                      <span>{formatPlatformName(platform)}</span>
                                    </button>
                                  );
                                })}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {selectedPlatforms.length} of {connectedPlatforms.length} platforms selected
                              </p>
                            </div>
                          )}

                          <div className="flex items-center space-x-2" data-tour="schedule-toggle">
                            <Switch
                              id="schedule-immediately"
                              checked={scheduleImmediately}
                              onCheckedChange={setScheduleImmediately}
                            />
                            <Label htmlFor="schedule-immediately" className="text-sm">
                              Schedule immediately
                            </Label>
                          </div>

                          <div className="flex justify-end pt-2">
                            <button
                              onClick={createPosts}
                              disabled={isCreating || hasConnectedAccounts === false || selectedPlatforms.length === 0}
                              className="flex-1 flex items-center justify-center space-x-2 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg hover:shadow-xl transition-all duration-200 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{
                                background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                                backgroundSize: '200% 200%',
                                backgroundPosition: 'left center',
                                boxShadow: '0 10px 25px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
                              }}
                              onMouseEnter={(e) => {
                                if (!isCreating) {
                                  e.currentTarget.style.backgroundPosition = 'right center';
                                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(48, 179, 221, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
                                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundPosition = 'left center';
                                e.currentTarget.style.boxShadow = '0 10px 25px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
                                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                              }}
                            >
                              {isCreating ? (
                                <>
                                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  <span>Creating...</span>
                                </>
                              ) : hasConnectedAccounts === false ? (
                                <>
                                  <AlertCircle className="w-3 h-3" />
                                  <span>Connect Accounts</span>
                                </>
                              ) : selectedPlatforms.length === 0 ? (
                                <>
                                  <Target className="w-3 h-3" />
                                  <span>Select Platforms</span>
                                </>
                              ) : (
                                <>
                                  <Zap className="w-3 h-3" />
                                  <span>Create Posts</span>
                                  <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs font-medium">
                                    {getTaskCost('One-Click Creator')} credits
                                  </span>
                                </>
                              )}
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </motion.div>
                )}



                {/* Step 4: Posts Complete */}
                {currentStep === 'complete' && postsResult && !isCreating && (
                  <motion.div
                    key="complete"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* Success Message */}
                    <Card className="relative overflow-hidden bg-card/50 backdrop-blur-xl border border-border/50">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>
                      <CardContent className="relative pt-6">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-500/10 rounded-full flex items-center justify-center">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <h3 className="text-lg font-medium">Posts Ready</h3>
                              <p className="text-sm text-muted-foreground">
                                {postsResult.generated_posts.length} posts scheduled across {new Set(postsResult.generated_posts.map(p => p.platform)).size} platforms
                              </p>

                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>



                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                      <Button onClick={startNewCreation} variant="outline" className="flex-1">
                        <Wand2 className="w-4 h-4 mr-2" />
                        Create New Posts
                      </Button>
                      <Button
                        onClick={() => {
                          if (postsResult.was_scheduled) {
                            // Posts were scheduled - go to calendar
                            navigate('/app/schedule');
                          } else {
                            // Posts are drafts - go to post group for scheduling
                            if (postsResult.post_group_id) {
                              navigate(`/app/posts?group=${postsResult.post_group_id}`);
                            } else {
                              navigate('/app/posts');
                            }
                          }
                        }}
                        className="flex-1 bg-gradient-to-r from-primary to-purple-600"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        {postsResult.was_scheduled ? 'View Schedule' : 'Edit or Schedule Posts'}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Right Panel - Enhanced Preview */}
            <div className="lg:col-span-7 grid grid-cols-1 gap-6">
              <AnimatePresence mode="wait">

                {/* Upload State */}
                {currentStep === 'upload' && (
                  <motion.div
                    key="upload-preview"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/90 via-slate-50/80 to-blue-50/70 dark:from-card/80 dark:to-card/40 backdrop-blur-xl border border-slate-200/50 dark:border-border/30 shadow-xl shadow-slate-500/10 dark:shadow-slate-500/5 h-[600px]">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5"></div>

                      {/* Header */}
                      <div className="relative p-6 border-b border-slate-200/50 dark:border-slate-700/50">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                            {creationMode === 'image' ? <Upload className="w-5 h-5 text-white" /> : <Palette className="w-5 h-5 text-white" />}
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Ready to Create</h3>
                            <p className="text-sm text-slate-600 dark:text-slate-400">Your posts preview will appear here</p>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="relative flex items-center justify-center h-full p-8">
                        <div className="text-center space-y-8 max-w-md">
                          {/* Animated Icon */}
                          <motion.div
                            className="w-32 h-32 mx-auto bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl flex items-center justify-center relative"
                            animate={{
                              scale: [1, 1.05, 1],
                              rotate: [0, 1, 0]
                            }}
                            transition={{
                              duration: 4,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}
                          >
                            <img
                              src="/icon-light.png"
                              alt="Quixocial"
                              className="w-20 h-20 sm:w-24 sm:h-24 object-contain"
                            />

                            {/* Rotating glow rings around the logo */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <motion.div
                                className="w-28 h-28 sm:w-32 sm:h-32 border-2 border-blue-400/30 rounded-full"
                                animate={{ rotate: 360 }}
                                transition={{
                                  duration: 8,
                                  repeat: Infinity,
                                  ease: "linear"
                                }}
                              />
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <motion.div
                                className="w-36 h-36 sm:w-40 sm:h-40 border border-purple-400/20 rounded-full"
                                animate={{ rotate: -360 }}
                                transition={{
                                  duration: 12,
                                  repeat: Infinity,
                                  ease: "linear"
                                }}
                              />
                            </div>

                            {/* Pulsing glow effect behind logo */}
                            <div className="absolute inset-0 flex items-center justify-center -z-10">
                              <motion.div
                                className="w-24 h-24 sm:w-28 sm:h-28 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-2xl"
                                animate={{
                                  scale: [1, 1.2, 1],
                                  opacity: [0.3, 0.6, 0.3]
                                }}
                                transition={{
                                  duration: 3,
                                  repeat: Infinity,
                                  ease: "easeInOut"
                                }}
                              />
                            </div>
                          </motion.div>

                          {/* Text Content */}
                          <div className="space-y-4">
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                              {creationMode === 'image' ? 'Upload an Image to Begin' : 'Create from Your Brandkit'}
                            </h3>
                            <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed">
                              {creationMode === 'image'
                                ? 'Your brand-customized posts will appear here in just 60 seconds'
                                : 'Professional brand-customized post will be generated from your brand information'
                              }
                            </p>
                          </div>

                          {/* Feature Pills */}

                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Processing State */}
                {currentStep === 'create' && (
                  <motion.div
                    key="processing"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card className="relative overflow-hidden bg-card/50 backdrop-blur-xl border border-border/50 h-[600px]">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-500/10 to-pink-500/10"></div>
                      <CardContent className="relative flex items-center justify-center h-full">
                        <div className="text-center space-y-8">
                          {/* Animated Processing Icon */}
                          <div className="relative">
                            <div className="w-32 h-32 mx-auto relative">
                              {/* Outer rotating ring */}
                              <div className="absolute inset-0 border-4 border-primary/20 rounded-full animate-spin"></div>
                              <div className="absolute inset-2 border-4 border-purple-500/30 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '3s' }}></div>
                              <div className="absolute inset-4 border-4 border-pink-500/40 rounded-full animate-spin" style={{ animationDuration: '2s' }}></div>

                              {/* Center processing icon */}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-primary via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center animate-pulse">
                                  <Zap className="w-8 h-8 text-white" />
                                </div>
                              </div>

                              {/* Floating particles */}
                              <div className="absolute -top-2 -right-2 w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                              <div className="absolute -bottom-2 -left-2 w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
                              <div className="absolute top-1/2 -left-4 w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '1s' }}></div>
                              <div className="absolute top-1/4 -right-4 w-3 h-3 bg-yellow-500 rounded-full animate-bounce" style={{ animationDelay: '1.5s' }}></div>
                            </div>
                          </div>

                          {/* Processing text */}
                          <div className="space-y-4">
                            <h3 className="text-2xl font-bold bg-gradient-to-r from-primary via-purple-600 to-pink-600 bg-clip-text text-transparent">
                              Creating Your Posts
                            </h3>
                            <div className="space-y-2">
                              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                                <span>
                                  {creationMode === 'brand'
                                    ? 'Analyzing your brand information...'
                                    : 'Analyzing your brand and image...'
                                  }
                                </span>
                              </div>
                              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                                <span>
                                  {creationMode === 'brand'
                                    ? 'Generating brand-focused content...'
                                    : 'Generating posts...'
                                  }
                                </span>
                              </div>
                              <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                                <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                                <span>Creating platform-specific content...</span>
                              </div>
                              {scheduleImmediately && (
                                <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
                                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
                                  <span>Scheduling your posts...</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className="w-full max-w-md mx-auto">
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-primary via-purple-500 to-pink-500 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${Math.min(creationProgress, 100)}%` }}
                              ></div>
                            </div>
                            <p className="text-xs text-center text-muted-foreground mt-2">
                              {Math.round(creationProgress)}% complete
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Analyze State - Simple preview */}
                {currentStep === 'analyze' && (creationMode === 'image' ? uploadedFile : true) && (
                  <motion.div
                    key="analyze-preview"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                  >

                    <Card className="relative overflow-hidden bg-card/50 backdrop-blur-xl border border-border/50 h-[500px]">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>
                      <CardHeader className="relative">
                        <CardTitle className="flex items-center space-x-2">
                          <Eye className="w-5 h-5" />
                          <span>Ready to Transform</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="relative flex items-center justify-center h-full">
                        <div className="text-center space-y-6">
                          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-primary/10 to-purple-500/10 rounded-2xl flex items-center justify-center">
                            <Wand2 className="w-12 h-12 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold mb-2">
                              {creationMode === 'brand' ? 'Brand-Based Creation Ready' : 'Enhancement Ready'}
                            </h3>
                            <p className="text-muted-foreground">
                              {creationMode === 'brand'
                                ? 'Professional content will be generated from your brand information'
                                : 'Your image will be transformed into professional content'
                              }
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {/* Complete State - Multiple Post Previews */}
                {currentStep === 'complete' && postsResult && !isCreating && (
                  <motion.div
                    key="complete-preview"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* Header */}
                    <Card className="relative overflow-hidden bg-card/50 backdrop-blur-xl border border-border/50">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>
                      <CardHeader className="relative">
                        <CardTitle className="flex items-center space-x-2">
                          <Eye className="w-5 h-5" />
                          <span>Posts Preview</span>
                        </CardTitle>
                        <p className="text-muted-foreground text-sm">
                          {postsResult.generated_posts.length} posts ready and scheduled across {new Set(postsResult.generated_posts.map(p => p.platform)).size} platforms
                        </p>
                      </CardHeader>
                    </Card>

                    {/* Multiple Post Previews Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 justify-items-center">
                      {postsResult.generated_posts.map((post, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="w-full max-w-xs"
                        >
                          <Card className="relative overflow-hidden bg-card/50 backdrop-blur-xl border border-border/50 h-full">
                            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5"></div>

                            {/* Platform Header */}
                            <CardHeader className="relative pb-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  {getPlatformIcon(post.platform)}
                                  <div>
                                    <p className="font-medium text-xs">{formatPlatformName(post.platform)}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {new Date(post.scheduled_at).toLocaleDateString()} at {new Date(post.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-xs bg-muted px-1.5 py-0.5 rounded capitalize">
                                  {post.content_type.replace('_', ' ')}
                                </div>
                              </div>
                            </CardHeader>

                            <CardContent className="relative space-y-2 px-4 pb-3">
                              {/* Enhanced Image */}
                              <div className="relative rounded-lg overflow-hidden">
                                {/* Use media_file_id to construct the image URL */}
                                {post.media_file_id || postsResult.enhanced_image_url ? (
                                  <div className="relative">
                                    {post.media_file_id ? (
                                      <AuthenticatedImage
                                        src={`/api/v1/media/${post.media_file_id}/download`}
                                        clientId={currentClient.id}
                                        alt="Generated Advertisement"
                                        className="w-full h-auto object-contain rounded-lg"
                                      />
                                    ) : (
                                      <img
                                        src={`${getApiUrl()}${postsResult.enhanced_image_url}`}
                                        alt="Generated Advertisement"
                                        className="w-full h-auto object-contain"
                                        style={{ imageRendering: 'high-quality' }}
                                      />
                                    )}
                                  </div>
                                ) : (
                                  <div className="h-48 flex items-center justify-center bg-muted rounded-lg">
                                    <div className="text-center">
                                      <Sparkles className="w-8 h-8 text-primary mx-auto mb-2" />
                                      <p className="text-sm text-muted-foreground">
                                        {creationMode === 'brand' ? 'Generated Image' : 'Enhanced Image'}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Post Caption */}
                              <div className="space-y-2">
                                <div className="flex items-start space-x-2">
                                  {/* Brand Logo or Initials */}
                                  {(() => {
                                    // Get logo URL from brand analysis or client
                                    const logoUrl = brandAnalysis?.logo_url || currentClient?.logo_url;
                                    const logoMediaId = brandAnalysis?.logo_media_id;
                                    const brandName = brandAnalysis?.brand_name || currentClient?.brand_name || currentClient?.name || 'Your Brand';
                                    
                                    // Construct logo URL from media ID if available
                                    const finalLogoUrl = logoUrl || (logoMediaId ? `/api/v1/media/${logoMediaId}/file` : null);
                                    
                                    if (finalLogoUrl) {
                                      return (
                                        <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 mt-0.5">
                                          <img 
                                            src={finalLogoUrl} 
                                            alt="Brand Logo"
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                              // Fallback to initials if logo fails to load
                                              const target = e.target as HTMLImageElement;
                                              const parent = target.parentElement;
                                              if (parent) {
                                                const initials = brandName.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase();
                                                parent.innerHTML = `<div class="w-5 h-5 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center flex-shrink-0"><span class="text-white font-bold text-xs">${initials}</span></div>`;
                                              }
                                            }}
                                          />
                                        </div>
                                      );
                                    } else {
                                      // Show initials
                                      const initials = brandName.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase();
                                      return (
                                        <div className="w-5 h-5 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                          <span className="text-white font-bold text-xs">{initials}</span>
                                        </div>
                                      );
                                    }
                                  })()}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium">
                                      {brandAnalysis?.brand_name || currentClient?.brand_name || currentClient?.name || 'Your Brand'}
                                    </p>
                                    <p className="text-xs text-foreground leading-relaxed whitespace-pre-wrap break-words">
                                      {post.content}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Post Status */}
                              <div className="flex items-center justify-end pt-1.5 border-t border-border/50">
                                <span className="text-xs text-muted-foreground capitalize">
                                  {post.status}
                                </span>
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </div>

                  </motion.div>
                )}

              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
      
      {/* Upgrade Modal for locked features */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        title="Upgrade to Use One-Click Creator"
        message="The One-Click Creator feature is available on paid plans. Upgrade now to automatically generate branded social media content."
        feature="one-click-creator"
      />
    </ComingSoonWrapper>
  );
};

export default OneClickCreatorPage;